import {
  Button,
  Dropdown,
  MenuProps,
  Modal,
  notification,
  Pagination,
  Select,
  Table,
  Tooltip,
  DatePicker,
  Tag,
  Image,
  Space,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatData, formatedDate } from 'utils/common';
import moment from 'moment';
import dayjs from 'dayjs';
import PageTitle from '../../components/page-title';
import Filter from './component/filter';
import styles from './styles.module.scss';
import ModalCreateEditUser from './component/ModalCreateEditUser';
import CustomButton from 'components/custom-button';
import {
  BookOutlined,
  CalendarOutlined,
  CoffeeOutlined,
  CompassOutlined,
  EditOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
  MoreOutlined,
  PlusOutlined,
  ScheduleOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { shortenName } from '../../utils/short-name';
import DefaultImage from 'assets/images/default-img.png';
import { TYPE_DEPARTMENT } from 'constants/common';
import { MEMBER_GROUP_LABELS, MEMBER_ROLE_LABELS, STUDIO_BRANCH_LABELS } from './membership-labels';
import { ATTENDANCE_STATUS_LABEL } from 'pages/attendance-manager/component/filter';
import { buildUserLinks, useDropSearchParams } from './helpers/user-deep-link';
import VerifiedTick, { badgeOfUser } from 'components/verified-tick';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

function rolesOf(roles?: string | string[] | null): string[] {
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string')
    return roles
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
  return [];
}

/** "Xem nhanh": mở các trang nhân sự đã lọc sẵn theo người này (Ctrl+click → tab mới). */
function quickLinkItems(user: any): MenuProps['items'] {
  const links = buildUserLinks(user);
  return [
    {
      key: 'attendance',
      icon: <FieldTimeOutlined />,
      disabled: !links.attendance,
      label: links.attendance ? (
        <Link to={links.attendance}>Chấm công</Link>
      ) : (
        'Chấm công (chưa có TikTok ID/email)'
      ),
    },
    {
      key: 'leave',
      icon: <CoffeeOutlined />,
      label: <Link to={links.leaveRequests}>Nghỉ phép</Link>,
    },
    {
      key: 'overtime',
      icon: <HourglassOutlined />,
      label: <Link to={links.overtime}>Tăng ca</Link>,
    },
    {
      key: 'payslips',
      icon: <WalletOutlined />,
      label: <Link to={links.payslips}>Phiếu lương</Link>,
    },
    { type: 'divider' },
    {
      key: 'star-ledger',
      icon: <BookOutlined />,
      label: <Link to={links.starLedger}>Lịch sử Sao</Link>,
    },
  ];
}

export interface UserFilter {
  limit: number;
  page: number;
  username?: string;
  email?: string;
  tiktokUid?: string;
  tiktokLinked?: 'yes' | 'no';
  roles?: string;
  creatorPending?: 'yes';
  verifiedBadge?: 'PINK' | 'BLUE' | 'NONE';
  startDate?: string;
  endDate?: string;
}

const UserManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as UserFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);
  const [confirmTarget, setConfirmTarget] = useState<any>(null);
  const [confirmBranch, setConfirmBranch] = useState<'DANCE' | 'NPC' | undefined>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isOpenAttendanceModal, setIsOpenAttendanceModal] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceDays, setAttendanceDays] = useState<any[]>([]);
  const [attendanceMonth, setAttendanceMonth] = useState(moment().format('YYYY-MM'));
  const [attendanceUser, setAttendanceUser] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dropSearchParams = useDropSearchParams();
  const urlAction = searchParams.get('action');

  // Lối tắt từ Tổng quan: ?action=create mở "Thêm thành viên".
  useEffect(() => {
    if (!urlAction) return;
    if (urlAction === 'create') {
      setvalueUserSelected(null);
      setIsOpenModalEditAndCreate(true);
    } else if (urlAction === 'tiktok') {
      navigate('/data-import', { replace: true });
      return;
    }
    dropSearchParams('action');
  }, [urlAction, dropSearchParams, navigate]);

  const onFinish = (value: Partial<UserFilter>) => {
    setFilter(formatData({ ...initFilter, ...value }));
  };

  const confirmCreator = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.requestedMemberGroup === 'CREATOR_STUDIO' && !confirmBranch) {
      notification.warning({ message: 'Vui lòng chọn Dance Creator hoặc NPC Creator.' });
      return;
    }
    setConfirmLoading(true);
    try {
      await AxiosUtils.post(`${API.users}/${confirmTarget.id}/confirm-creator`, {
        ...(confirmBranch ? { studioCreatorBranch: confirmBranch } : {}),
      });
      setConfirmTarget(null);
      setConfirmBranch(undefined);
      setStatusUpdateData((v) => !v);
      notification.success({ message: 'Đã xác nhận Creator và đồng bộ nhóm Điểm SAO.' });
    } catch (error: any) {
      notification.error({ message: 'Không xác nhận được', description: error?.message || 'Vui lòng thử lại.' });
    } finally {
      setConfirmLoading(false);
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    // console.log('selectedRowKeys changed: ', newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.users, {
          ...filter,
        });

        const makeData = res?.data?.data?.map((user: any) => {
          return {
            ...user,
            about: user?.profile?.about || '',
          };
        });
        setDataResponse({ items: makeData, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '5%',
      ellipsis: true,
      key: 'id',
    },
    // Bảng có scroll.x → table-layout: fixed; mọi cột cần width để chữ không bị bẻ giữa chừng
    // (email/username dài cắt "…", rê chuột xem đủ) và ngày/số giữ trên một dòng.
    {
      title: 'TikTok ID',
      dataIndex: 'username',
      key: 'username',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'UID TikTok',
      dataIndex: 'tiktokUid',
      key: 'tiktokUid',
      width: 190,
      render: (value: string | null, record: any) =>
        value ? (
          <Tooltip
            title={
              record?.tiktokSyncedAt
                ? `Đồng bộ từ file TikTok lúc ${formatedDate(record.tiktokSyncedAt)}`
                : 'Admin nhập tay, chưa có trong file TikTok nào'
            }
          >
            <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {value}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Tag>Chưa gắn</Tag>
        ),
    },
    {
      title: 'Tên hiển thị',
      dataIndex: 'nickName',
      key: 'nickName',
      width: 190,
      // Tích Hồng/Xanh + "Đang khoá" ngay sau tên (docs/xac-minh-danh-tinh §5.4).
      render: (value: string, record: any) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value}
          </span>
          <VerifiedTick badge={badgeOfUser(record)} size={14} />
          {record.isAccountDisabled ? (
            <Tag color="error" style={{ marginInlineEnd: 0 }}>
              Đang khoá
            </Tag>
          ) : null}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 230,
      ellipsis: true,
    },
    {
      title: 'Vai trò',
      dataIndex: 'roles',
      key: 'roles',
      width: 140,
      render: (value) => (
        <Space size={4} wrap>
          {rolesOf(value).map((role) => (
            <Tag key={role}>{MEMBER_ROLE_LABELS[role] || role}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Nhóm đăng ký',
      key: 'requestedMemberGroup',
      width: 185,
      render: (_, user) => {
        const requested = user.requestedMemberGroup || 'USER';
        if (!['USER', 'CREATOR', 'STUDIO_CREATOR'].includes(user.roles)) return '—';
        const isPending = user.roles === 'USER' && requested !== 'USER';
        return (
          <Space size={4} wrap>
            <Tag color={isPending ? 'gold' : 'default'}>
              {MEMBER_GROUP_LABELS[requested] || requested}
            </Tag>
            {isPending && <Tag color="gold">{user.isVerifyAccount ? 'Chờ duyệt' : 'Chưa xác minh email'}</Tag>}
            {user.roles === 'STUDIO_CREATOR' && (
              <Tag color="purple">{STUDIO_BRANCH_LABELS[user.studioCreatorBranch] || 'Chưa phân nhánh'}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Bộ phận',
      dataIndex: 'department',
      key: 'department',
      width: 130,
      render: (value, record) => {
        if (record.roles === 'ADMIN') return <span>Admin</span>;
        const actions: Record<TYPE_DEPARTMENT, string> = {
          [TYPE_DEPARTMENT.HR]: 'Nhân sự',
          [TYPE_DEPARTMENT.TECHNICAL]: 'Kỹ thuật',
          [TYPE_DEPARTMENT.MAKEUP]: 'Makeup',
          [TYPE_DEPARTMENT.MC]: 'MC',
          [TYPE_DEPARTMENT.IDOL]: 'Creator',
          [TYPE_DEPARTMENT.CONTENT]: 'Content',
          [TYPE_DEPARTMENT.STYLE_LIST]: 'Style List',
          [TYPE_DEPARTMENT.DANCE_TEACHER]: 'Dance Teacher',
          [TYPE_DEPARTMENT.MANAGER]: 'QL Vận Hành',
        };
        return (
          <Tooltip title={actions[value as TYPE_DEPARTMENT] || value}>
            <span>{actions[value as TYPE_DEPARTMENT] || ''}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 140,
      render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>,
    },
    {
      title: 'Data cá nhân',
      dataIndex: 'about',
      key: 'about',
      width: 170,
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 20) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Nổi bật',
      dataIndex: 'isOutStanding',
      key: 'isOutStanding',
      width: 90,
      render: (value) => <span>{value ? 'Có' : 'Không'}</span>,
    },
    {
      title: 'VIP',
      dataIndex: 'isVip',
      key: 'isVip',
      width: 80,
      render: (value) => <span>{value ? 'Có' : 'Không'}</span>,
    },
    {
      title: 'Ngày đăng ký',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 205,
      key: 'action',
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa"
              onClick={() => handelOpenCreateEditModal(record)}
            />
          </Tooltip>
          {record.roles === 'USER' && record.requestedMemberGroup !== 'USER' &&
            !!record.requestedMemberGroup && (
              <Tooltip title={record.isVerifyAccount ? 'Xác nhận nhóm Creator' : 'Đợi người dùng xác minh email'}>
                <Button
                  size="small"
                  type="primary"
                  disabled={!record.isVerifyAccount || record.isAccountDisabled}
                  onClick={() => { setConfirmTarget(record); setConfirmBranch(undefined); }}
                >
                  Xác nhận
                </Button>
              </Tooltip>
            )}
          <Dropdown menu={{ items: quickLinkItems(record) }} trigger={['click']}>
            <Button
              type="text"
              icon={<CompassOutlined />}
              title="Xem nhanh"
              aria-label="Xem nhanh"
            />
          </Dropdown>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'month', icon: <CalendarOutlined />, label: 'Bảng công theo tháng' },
                { key: 'assign', icon: <ScheduleOutlined />, label: 'Tạo lịch làm hôm nay' },
              ],
              onClick: ({ key }) => {
                if (key === 'month') openAttendanceModal(record);
                if (key === 'assign') confirmGenerateAssignments(record);
              },
            }}
          >
            <Button type="text" icon={<MoreOutlined />} title="Thêm" aria-label="Thêm thao tác" />
          </Dropdown>
        </Space>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteUsers, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      setSelectedRowKeys([]);
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
    } catch (error) {
      console.log(error);
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  const fetchAttendanceDays = async (userId: number, monthStr: string) => {
    setAttendanceLoading(true);
    try {
      const res: any = await AxiosUtils.get('/attendance/admin/month/days', {
        userId,
        month: monthStr,
        includeRecords: true,
      });
      const items = res?.data?.data || [];
      setAttendanceDays(items);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const openAttendanceModal = (user: any) => {
    setAttendanceUser(user);
    setIsOpenAttendanceModal(true);
    fetchAttendanceDays(user.id, attendanceMonth);
  };

  const closeAttendanceModal = () => {
    setIsOpenAttendanceModal(false);
    setAttendanceUser(null);
  };

  const onChangeAttendanceMonth = (value: any) => {
    const monthStr = value ? dayjs(value).format('YYYY-MM') : dayjs().format('YYYY-MM');
    setAttendanceMonth(monthStr);
    if (attendanceUser?.id) {
      fetchAttendanceDays(attendanceUser.id, monthStr);
    }
  };

  // POST /attendance/assignments/generate cho riêng người này, ngày hôm nay
  // (trước đây là mục "Setting" không rõ nghĩa).
  const confirmGenerateAssignments = (record: any) => {
    Modal.confirm({
      title: 'Tạo lịch làm hôm nay?',
      content: `Tạo lịch làm ngày ${moment().format('DD/MM/YYYY')} cho ${
        record?.nickName || record?.username || record?.email || `#${record?.id}`
      } theo ca mặc định của người này.`,
      okText: 'Tạo lịch',
      cancelText: 'Huỷ',
      onOk: () => handleGenerateAssignments(record),
    });
  };

  const handleGenerateAssignments = async (record: any) => {
    try {
      const date = moment().format('YYYY-MM-DD');
      await AxiosUtils.post(`${API.generateAssignments}?date=${date}&userId=${record.id}`, {
        date,
        userId: record.id,
      });
      notification.success({
        message: 'Thành công',
        description: 'Đã tạo lịch làm hôm nay.',
        duration: 4,
      });
    } catch (error) {
      console.log(error);
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra!',
        duration: 4,
      });
    }
  };

  const handleExportExcel = async (allMonths: boolean) => {
    if (!attendanceUser?.id) return;
    try {
      setExportLoading(true);
      const params: any = { userId: attendanceUser.id };
      if (allMonths) {
        params.month = 'all';
      } else {
        params.month = attendanceMonth;
      }

      const response: any = await AxiosUtils.get(API.attendanceExportExcel, params, undefined, {
        responseType: 'blob',
      });

      const contentDisposition = response?.headers?.['content-disposition'];
      let filename = `attendance-user-${attendanceUser.id}-${
        allMonths ? 'all' : attendanceMonth
      }.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], {
        type:
          response?.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({ message: 'Thành công', description: 'Xuất file thành công!' });
    } catch (error) {
      console.error(error);
      notification.error({ message: 'Không thành công', description: 'Xuất file thất bại!' });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div>
      <PageTitle
        title="Thành viên"
        extra={
          <Space wrap>
            <CustomButton
              onClick={() => handelOpenCreateEditModal(null)}
              bgColor="primary-green"
              text="Thêm thành viên"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <div className={styles.filterContainer}>
        <Filter onSubmit={onFinish} />
      </div>
      <div className="custom-table custom-form">
        <div className={styles.wrapCounterUser}>
          <Button
            type="primary"
            onClick={handelOpenDeleteModal}
            disabled={!hasSelected}
            loading={loading}
          >
            Xóa
          </Button>
          <span>Số lượng thành viên: {dataResponse?.totalCount || 0}</span>
        </div>

        <span style={{ marginLeft: 8 }}>
          {hasSelected ? `Đã chọn ${selectedRowKeys.length} thành viên` : ''}
        </span>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
           scroll={{ x: 2150 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>

      {isOpenModalEditAndCreate && (
        <ModalCreateEditUser
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title={`Xác nhận ${MEMBER_GROUP_LABELS[confirmTarget?.requestedMemberGroup] || 'Creator'}`}
        open={!!confirmTarget}
        okText="Xác nhận"
        okButtonProps={{ loading: confirmLoading, disabled: confirmTarget?.requestedMemberGroup === 'CREATOR_STUDIO' && !confirmBranch }}
        onOk={confirmCreator}
        onCancel={() => { setConfirmTarget(null); setConfirmBranch(undefined); }}
      >
        <p>{confirmTarget?.nickName || confirmTarget?.username} đã xác minh email. Sau khi xác nhận, quyền Creator sẽ được bật.</p>
        {confirmTarget?.requestedMemberGroup === 'CREATOR_STUDIO' ? (
          <div>
            <p>Nhánh Studio Creator</p>
            <Select
              placeholder="Chọn nhánh"
              value={confirmBranch}
              style={{ width: '100%' }}
              options={[{ value: 'DANCE', label: 'Dance Creator' }, { value: 'NPC', label: 'NPC Creator' }]}
              onChange={setConfirmBranch}
            />
            <p>Tham gia Điểm SAO theo nhánh đã chọn.</p>
          </div>
        ) : (
          <p>Online Creator xem data hằng ngày, bảng xếp hạng tháng và không tham gia Điểm SAO.</p>
        )}
      </Modal>
      <Modal
        title="Xóa thành viên"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} thành viên đã chọn?</p>
      </Modal>

      <Modal
        title={`Bảng công theo tháng — ${attendanceUser?.username || ''}`}
        open={isOpenAttendanceModal}
        footer={null}
        onCancel={closeAttendanceModal}
        width={1100}
      >
        <div className="custom-form" style={{ marginBottom: 16, display: 'flex', gap: '16px' }}>
          <DatePicker
            picker="month"
            allowClear={false}
            style={{ width: 240 }}
            value={dayjs(attendanceMonth, 'YYYY-MM')}
            onChange={onChangeAttendanceMonth}
          />
          <Button type="primary" onClick={() => handleExportExcel(false)} loading={exportLoading}>
            Xuất Excel tháng này
          </Button>
          <Button type="default" onClick={() => handleExportExcel(true)} loading={exportLoading}>
            Xuất Excel tất cả tháng
          </Button>
        </div>
        <Table
          rowKey={(record) => record.date}
          loading={attendanceLoading}
          dataSource={attendanceDays}
          pagination={false}
          columns={[
            {
              title: 'Ngày',
              dataIndex: 'date',
              key: 'date',
              render: (value: string) => (value ? moment(value).format('YYYY-MM-DD') : ''),
            },
            {
              title: 'Cuối tuần',
              dataIndex: 'weekend',
              key: 'weekend',
              render: (value: boolean) => (
                <Tag color={value ? 'geekblue' : 'default'}>{value ? 'Có' : 'Không'}</Tag>
              ),
            },
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              key: 'status',
              render: (value: string) => (
                <Tag color={value === 'ok' ? 'green' : 'default'}>
                  {value === 'ok' ? 'Đủ công' : value === 'missing' ? 'Thiếu công' : value}
                </Tag>
              ),
            },
            {
              title: 'Số bản ghi',
              dataIndex: 'recordsCount',
              key: 'recordsCount',
            },
            {
              title: 'Bản ghi duyệt',
              dataIndex: 'approvedRecordsCount',
              key: 'approvedRecordsCount',
            },
            {
              title: 'Phút làm việc',
              dataIndex: 'workingMinutesTotal',
              key: 'workingMinutesTotal',
            },
          ]}
          expandable={{
            expandedRowRender: (record: any) => (
              <Table
                rowKey={(r: any) => r.id}
                dataSource={record.records || []}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Ca', dataIndex: 'shiftName', key: 'shiftName' },
                  {
                    title: 'Checkin',
                    dataIndex: 'checkinTime',
                    key: 'checkinTime',
                    render: (v: string) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : ''),
                  },
                  {
                    title: 'Checkout',
                    dataIndex: 'checkoutTime',
                    key: 'checkoutTime',
                    render: (v: string) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : ''),
                  },
                  {
                    title: 'Trạng thái',
                    dataIndex: 'status',
                    key: 'status',
                    render: (v: string) => (
                      <Tag
                        color={
                          v === 'normal'
                            ? 'green'
                            : v === 'late'
                            ? 'volcano'
                            : v === 'early'
                            ? 'gold'
                            : v === 'late-early'
                            ? 'red'
                            : v === 'no-checkout'
                            ? 'geekblue'
                            : 'default'
                        }
                      >
                        {ATTENDANCE_STATUS_LABEL[v as keyof typeof ATTENDANCE_STATUS_LABEL] || v}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Ảnh checkin',
                    dataIndex: 'checkinImageUrl',
                    key: 'checkinImageUrl',
                    render: (v: string) => (
                      <Image
                        src={v || DefaultImage}
                        alt="checkin"
                        style={{ width: 40, height: 40, objectFit: 'cover' }}
                        preview={!!v}
                      />
                    ),
                  },
                  {
                    title: 'Ảnh checkout',
                    dataIndex: 'checkoutImageUrl',
                    key: 'checkoutImageUrl',
                    render: (v: string) => (
                      <Image
                        src={v || DefaultImage}
                        alt="checkout"
                        style={{ width: 40, height: 40, objectFit: 'cover' }}
                        preview={!!v}
                      />
                    ),
                  },

                  { title: 'Phút làm việc', dataIndex: 'workingMinutes', key: 'workingMinutes' },
                  {
                    title: 'Duyệt',
                    dataIndex: 'isApproved',
                    key: 'isApproved',
                    render: (v: boolean) => (v ? 'Có' : 'Không'),
                  },
                ]}
              />
            ),
          }}
        />
      </Modal>
    </div>
  );
};

export default UserManager;
